/**
 * OpportunityFilterChips - Mobile
 * 
 * Horizontal scrolling filter chips for opportunity status filtering.
 * Features clear visual hierarchy with status indicators.
 * Uses design tokens exclusively per Cursor Rules.
 */

import React from 'react';
import { View, ScrollView, Pressable, Text, StyleSheet } from 'react-native';
import { color, text, space, radius, touch } from '../../../shared/tokens/mobile';
import { OpportunityFilter } from '../../../hooks/usePortfolioOpportunitiesWithFilter';

interface FilterOption {
  key: OpportunityFilter;
  label: string;
  shortLabel?: string;
  statusColor: string;
}

const FILTER_OPTIONS: FilterOption[] = [
  { key: 'all', label: 'All', statusColor: color.textMuted },
  { key: 'evaluating', label: 'Evaluating', statusColor: color.info },
  { key: 'under_contract', label: 'Under Contract', shortLabel: 'Contract', statusColor: color.warning },
  { key: 'owned', label: 'Owned', statusColor: color.success },
  { key: 'selling', label: 'Selling', statusColor: color.warning },
  { key: 'sold', label: 'Sold', statusColor: color.textMuted },
  { key: 'passed', label: 'Passed', statusColor: color.disabled },
];

interface OpportunityFilterChipsProps {
  activeFilter: OpportunityFilter;
  onFilterChange: (filter: OpportunityFilter) => void;
  counts: Record<OpportunityFilter, number>;
}

export function OpportunityFilterChips({ 
  activeFilter, 
  onFilterChange, 
  counts 
}: OpportunityFilterChipsProps) {
  // Filter to only show options with counts (always show 'all')
  const visibleOptions = FILTER_OPTIONS.filter(
    (option) => counts[option.key] > 0 || option.key === 'all'
  );

  return (
    <View style={styles.wrapper}>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.container}
        style={styles.scroll}
      >
        {visibleOptions.map((option) => {
          const count = counts[option.key] ?? 0;
          const isActive = activeFilter === option.key;
          const displayLabel = option.shortLabel || option.label;

          return (
            <Pressable
              key={option.key}
              onPress={() => onFilterChange(option.key)}
              style={({ pressed }) => [
                styles.chip,
                isActive && styles.chipActive,
                pressed && styles.chipPressed,
              ]}
              accessibilityRole="button"
              accessibilityState={{ selected: isActive }}
              accessibilityLabel={`${option.label}, ${count} ${count === 1 ? 'opportunity' : 'opportunities'}`}
            >
              {/* Status indicator dot */}
              {option.key !== 'all' && (
                <View 
                  style={[
                    styles.statusDot, 
                    { backgroundColor: option.statusColor },
                    isActive && styles.statusDotActive,
                  ]} 
                />
              )}
              
              {/* Label */}
              <Text style={[styles.chipLabel, isActive && styles.chipLabelActive]}>
                {displayLabel}
              </Text>
              
              {/* Count badge */}
              <View style={[styles.countBadge, isActive && styles.countBadgeActive]}>
                <Text style={[styles.countText, isActive && styles.countTextActive]}>
                  {count}
                </Text>
              </View>
            </Pressable>
          );
        })}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    backgroundColor: color.background,
    borderBottomWidth: 1,
    borderBottomColor: color.border,
  },
  scroll: {
    flexGrow: 0,
  },
  container: {
    paddingHorizontal: space.md,
    paddingVertical: space.sm,
    gap: space.sm,
    alignItems: 'center',
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.xs,
    minHeight: touch.minimum,
    paddingHorizontal: space.md,
    paddingVertical: space.sm,
    borderRadius: radius.full,
    backgroundColor: color.surface,
    borderWidth: 1,
    borderColor: color.border,
  },
  chipActive: {
    backgroundColor: color.primaryLight,
    borderColor: color.primary,
  },
  chipPressed: {
    backgroundColor: color.backgroundSecondary,
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  statusDotActive: {
    // Keep same color when active
  },
  chipLabel: {
    fontSize: text.body.fontSize,
    fontWeight: text.bodyMedium.fontWeight,
    color: color.textSecondary,
    letterSpacing: -0.2,
  },
  chipLabelActive: {
    color: color.primary,
    fontWeight: '600',
  },
  countBadge: {
    minWidth: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: color.backgroundSecondary,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: space.xs,
  },
  countBadgeActive: {
    backgroundColor: color.primary,
  },
  countText: {
    fontSize: text.caption.fontSize,
    fontWeight: '600',
    color: color.textMuted,
  },
  countTextActive: {
    color: color.textInverse,
  },
});
